import React, { Suspense } from "react";
import MatchExplanation from "../../../components/product/MatchExplanation";
import ProductMatchInsights from "../../../components/product/ProductMatchInsights";
import useIdentityCoach from "../../../hooks/useIdentityCoach";
import {
  ICONS,
  getDimensionBarClass,
} from "../helpers/productDetailViewHelpers";

const ProductDeeperInsightsSection = ({
  styles,
  wizardData,
  specs = [],
  hasInsightsContext = false,
  product,
  heroMatchData = null,
  deeperInsightsExpanded,
  setDeeperInsightsExpanded,
  enhancedMatchData,
  matchLoading,
  similarProductsCount = 0,
  heroSummaryShown = false,
  lowConfidence = false,
  identityCompleteness = null,
  categoryName = null,
  BudgetAnalysis,
  DecisionConfidence,
  MatchVisualization,
}) => {
  const { coaching: confidenceCoaching } = useIdentityCoach({
    enabled: Boolean(
      product && hasInsightsContext && lowConfidence && enhancedMatchData,
    ),
    identityCompleteness,
    productName: product?.name,
    categoryName,
    lowConfidence: true,
    reason: "low_confidence",
  });

  if (!product || !hasInsightsContext) return null;

  const confidenceCoachingTip =
    lowConfidence && confidenceCoaching?.questions?.[0]
      ? `${confidenceCoaching.questions[0].question} ${confidenceCoaching.questions[0].why || ""}`.trim()
      : confidenceCoaching?.headline || null;

  const showInlineExplanation = !heroSummaryShown;

  return (
    <div className={styles.deeperInsightsSection}>
      <h3 className={styles.deeperInsightsTitle}>More Insights</h3>

      {showInlineExplanation && (
        <div className={styles.sectionSpacing}>
          <MatchExplanation
            product={product}
            wizardData={wizardData}
            specs={specs}
            matchData={enhancedMatchData}
            matchLoading={matchLoading}
            usePrefetchedMatch
            heroSummaryShown={false}
          >
            {wizardData?.budget && (
              <BudgetAnalysis product={product} wizardData={wizardData} embedded />
            )}
          </MatchExplanation>
        </div>
      )}

      {heroSummaryShown && !deeperInsightsExpanded && (
        <div className={styles.expandButtonContainer}>
          <button
            type="button"
            className={styles.expandInsightsButton}
            onClick={() => setDeeperInsightsExpanded(true)}
          >
            Show Detailed Breakdown
            <span className={styles.expandIcon}>▼</span>
          </button>
        </div>
      )}

      {heroSummaryShown && deeperInsightsExpanded && (
        <Suspense fallback={<div className={styles.loading}>Loading insights...</div>}>
          <>
            {heroMatchData && (
              <div className={styles.sectionSpacing}>
                <ProductMatchInsights
                  matchData={heroMatchData}
                  iconsMap={ICONS}
                  getDimensionBarClass={(score) =>
                    getDimensionBarClass(score, styles)
                  }
                  dimensionsOnly
                />
              </div>
            )}

            <div className={styles.sectionSpacing}>
              <MatchExplanation
                product={product}
                wizardData={wizardData}
                specs={specs}
                matchData={enhancedMatchData}
                matchLoading={matchLoading}
                usePrefetchedMatch
                heroSummaryShown
              >
                {wizardData?.budget && (
                  <BudgetAnalysis
                    product={product}
                    wizardData={wizardData}
                    embedded
                  />
                )}
              </MatchExplanation>
            </div>

            {enhancedMatchData && (
              <div className={styles.sectionSpacing}>
                <DecisionConfidence
                  matchData={enhancedMatchData}
                  wizardData={wizardData}
                  alternativesCount={similarProductsCount}
                  similarPurchases={null}
                  coachingTip={confidenceCoachingTip}
                />
              </div>
            )}

            {enhancedMatchData && (
              <div className={styles.sectionSpacing}>
                <MatchVisualization
                  product={product}
                  wizardData={wizardData}
                  matchData={enhancedMatchData}
                />
              </div>
            )}

            <div className={styles.expandButtonContainer}>
              <button
                type="button"
                className={styles.collapseInsightsButton}
                onClick={() => setDeeperInsightsExpanded(false)}
              >
                Show Less
                <span className={styles.collapseIcon}>▲</span>
              </button>
            </div>
          </>
        </Suspense>
      )}

      {!heroSummaryShown && !deeperInsightsExpanded && (
        <div className={styles.expandButtonContainer}>
          <button
            type="button"
            className={styles.expandInsightsButton}
            onClick={() => setDeeperInsightsExpanded(true)}
          >
            Show More Insights
            <span className={styles.expandIcon}>▼</span>
          </button>
        </div>
      )}

      {!heroSummaryShown && deeperInsightsExpanded && (
        <Suspense fallback={<div className={styles.loading}>Loading insights...</div>}>
          <>
            {enhancedMatchData && (
              <div className={styles.sectionSpacing}>
                <DecisionConfidence
                  matchData={enhancedMatchData}
                  wizardData={wizardData}
                  alternativesCount={similarProductsCount}
                  similarPurchases={null}
                  coachingTip={confidenceCoachingTip}
                />
              </div>
            )}

            {enhancedMatchData && (
              <div className={styles.sectionSpacing}>
                <MatchVisualization
                  product={product}
                  wizardData={wizardData}
                  matchData={enhancedMatchData}
                />
              </div>
            )}

            <div className={styles.expandButtonContainer}>
              <button
                type="button"
                className={styles.collapseInsightsButton}
                onClick={() => setDeeperInsightsExpanded(false)}
              >
                Show Less
                <span className={styles.collapseIcon}>▲</span>
              </button>
            </div>
          </>
        </Suspense>
      )}
    </div>
  );
};

export default ProductDeeperInsightsSection;
